import { useState, useEffect, useRef } from 'react';
import { authenticatedFetch } from '@/lib/auth';
import { CheckCircle2, AlertTriangle, Loader2, Building2, RefreshCw } from 'lucide-react';

export interface BrregData {
  orgnr: string;
  name: string;
  org_form?: string;
  registered_date?: string;
  naering?: string;
  address?: string | null;
  homepage?: string | null;
  email?: string | null;
  slettet?: boolean;
  konkurs?: boolean;
}

interface Props {
  /** Org.nr som brukeren har skrevet inn (9 siffer utløser oppslag). */
  orgnr: string;
  /** Navn brukeren har skrevet inn (for mismatch-sammenligning). */
  currentName?: string;
  /** Adresse brukeren har skrevet inn (for mismatch-sammenligning). */
  currentAddress?: string;
  /** Kalles med { name, address } når bruker klikker "Bruk data fra Brreg". */
  onApplyBrreg: (data: { name: string; address: string | null }) => void;
  /** Compact-modus (mindre padding, brukes i redigeringsmodaler). */
  compact?: boolean;
  /** Kalles ved state-endring så parent kan disable lagre-knapp. */
  onStateChange?: (state: BrregLookupState) => void;
  /** Scope for test-nr-whitelist: 'member' = 123456789, 'study_association' = 012345678 */
  scope?: 'member' | 'study_association';
}

export type BrregLookupState = 'idle' | 'loading' | 'found' | 'not_found' | 'error';

// Reserverte testnummer — speiler backend utils/orgnr-validator.js
const TEST_ORGNR_MEMBER = '123456789';
const TEST_ORGNR_STUDY_ASSOCIATION = '012345678';

/**
 * Automatisk Brreg-oppslag basert på org.nr.
 * Debounced 500ms — utløses når 9 siffer skrevet.
 * Viser oppslag-status, mismatch-varsling, og "Bruk Brreg-data"-knapp.
 */
export function BrregOrgLookup({ orgnr, currentName, currentAddress, onApplyBrreg, compact = false, onStateChange, scope }: Props) {
  const [state, setState] = useState<BrregLookupState>('idle');
  useEffect(() => { if (onStateChange) onStateChange(state); }, [state, onStateChange]);
  const [brreg, setBrreg] = useState<BrregData | null>(null);
  const [isTestOrgnr, setIsTestOrgnr] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const timerRef = useRef<any>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const cleanOrgnr = String(orgnr || '').replace(/\s+/g, '');
    if (!/^\d{9}$/.test(cleanOrgnr)) {
      setState('idle');
      setBrreg(null);
      setIsTestOrgnr(false);
      return;
    }
    // Short-circuit for reserverte testnummer — hopp over Brreg-kallet
    const isMemberTest = cleanOrgnr === TEST_ORGNR_MEMBER && scope === 'member';
    const isSATest = cleanOrgnr === TEST_ORGNR_STUDY_ASSOCIATION && scope === 'study_association';
    if (isMemberTest || isSATest) {
      setIsTestOrgnr(true);
      setBrreg(null);
      setState('found');
      return;
    }
    setIsTestOrgnr(false);
    timerRef.current = setTimeout(() => {
      doLookup(cleanOrgnr);
    }, 500);
    return () => timerRef.current && clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgnr, scope]);

  async function doLookup(orgnrClean: string) {
    setState('loading');
    setErrorMsg('');
    try {
      const res = await authenticatedFetch(`/api/admin/brreg/lookup?orgnr=${orgnrClean}`);
      const data = await res.json();
      if (res.status === 404) {
        setState('not_found');
        setBrreg(null);
        return;
      }
      if (!res.ok || !data.success) {
        setState('error');
        setErrorMsg(data.error || `HTTP ${res.status}`);
        return;
      }
      setBrreg(data.brreg);
      setState('found');
    } catch (e: any) {
      setState('error');
      setErrorMsg(e.message);
    }
  }

  const nameNormalize = (s: string) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const nameMismatch = state === 'found' && currentName && brreg &&
    nameNormalize(currentName) !== nameNormalize(brreg.name);
  const addressMismatch = state === 'found' && currentAddress && brreg?.address &&
    nameNormalize(currentAddress) !== nameNormalize(brreg.address);

  if (state === 'idle') return null;

  const padding = compact ? 'p-2' : 'p-3';

  if (state === 'loading') {
    return (
      <div className={`mt-2 ${padding} rounded-md border border-gray-200 bg-gray-50 text-sm text-gray-600 flex items-center gap-2`}>
        <Loader2 className="h-4 w-4 animate-spin" /> Slår opp mot Brønnøysundregisteret…
      </div>
    );
  }

  if (state === 'not_found') {
    return (
      <div className={`mt-2 ${padding} rounded-md border border-amber-300 bg-amber-50 text-sm text-amber-900 flex items-start gap-2`}>
        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-medium">Organisasjonsnummeret finnes ikke i Brreg</div>
          <div className="text-xs mt-0.5">Dobbeltsjekk 9-sifret organisasjonsnummer. Du kan fortsatt lagre om du er sikker.</div>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className={`mt-2 ${padding} rounded-md border border-red-300 bg-red-50 text-sm text-red-800 flex items-start gap-2`}>
        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-medium">Brreg-oppslag feilet</div>
          <div className="text-xs mt-0.5">{errorMsg}</div>
          <button type="button" onClick={() => doLookup(orgnr.replace(/\s+/g, ''))}
            className="mt-1 text-xs text-red-700 underline inline-flex items-center gap-1">
            <RefreshCw className="h-3 w-3" /> Prøv igjen
          </button>
        </div>
      </div>
    );
  }

  // state === 'found' med test-orgnr (ikke faktisk Brreg-oppslag)
  if (isTestOrgnr) {
    const label = scope === 'member'
      ? `Reservert testnummer for medlemsorganisasjon (${TEST_ORGNR_MEMBER})`
      : `Reservert testnummer for studieforbund (${TEST_ORGNR_STUDY_ASSOCIATION})`;
    return (
      <div className={`mt-2 ${padding} rounded-md border border-blue-300 bg-blue-50 text-sm text-blue-900 flex items-start gap-2`}>
        <CheckCircle2 className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-medium">✓ Test-organisasjon</div>
          <div className="text-xs mt-0.5">{label} — tillates uten Brreg-oppslag. Må være unik (ikke registrert fra før).</div>
        </div>
      </div>
    );
  }

  // state === 'found' med reell Brreg-rad
  if (!brreg) return null;

  return (
    <div className={`mt-2 ${padding} rounded-md border ${nameMismatch || addressMismatch ? 'border-amber-300 bg-amber-50' : 'border-green-300 bg-green-50'}`}>
      <div className="flex items-start gap-2">
        {nameMismatch || addressMismatch ? (
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        ) : (
          <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1 text-sm">
          <div className={`font-medium ${nameMismatch || addressMismatch ? 'text-amber-900' : 'text-green-900'} flex items-center gap-2`}>
            <Building2 className="h-3 w-3" />
            {brreg.slettet ? 'SLETTET i Brreg — ' : brreg.konkurs ? 'KONKURS i Brreg — ' : ''}
            Fra Brønnøysund: {brreg.name}
          </div>
          {brreg.address && <div className="text-xs text-gray-700 mt-0.5">Adresse: {brreg.address}</div>}
          {brreg.org_form && <div className="text-xs text-gray-500 mt-0.5">{brreg.org_form}{brreg.naering ? ` · ${brreg.naering}` : ''}</div>}

          {(nameMismatch || addressMismatch) && (
            <div className="mt-2 text-xs text-amber-800">
              {nameMismatch && <div>⚠ Navn skiller seg fra det du har skrevet inn (Brreg: <strong>{brreg.name}</strong>, du: <strong>{currentName}</strong>)</div>}
              {addressMismatch && <div>⚠ Adresse skiller seg (Brreg: <strong>{brreg.address}</strong>, du: <strong>{currentAddress}</strong>)</div>}
              <div className="mt-1 italic">Brreg kan være utdatert — verifiser hvilket som er riktig.</div>
            </div>
          )}

          <div className="mt-2 flex gap-2 flex-wrap">
            <button type="button"
              onClick={() => onApplyBrreg({ name: brreg.name, address: brreg.address || null })}
              className="text-xs px-2 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 inline-flex items-center gap-1">
              <RefreshCw className="h-3 w-3" /> Bruk data fra Brreg
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
